import { fromNullable as optionFromNullable } from 'fp-ts/lib/Option';
import { Reducer } from 'react';
import { GroupedResults, SearchResults } from '../../lib/dto';
import { RemoteData } from '../RemoteData';

type Index = GroupedResults['Specific'];

export type LoadedState = {
  results: SearchResults;
  paginationStates: Record<Index, undefined | 'loading' | 'error'>;
};
export type State = RemoteData<LoadedState>;

export type Action =
  | { type: 'reset' }
  | { type: 'loading' }
  | { type: 'loaded'; payload: SearchResults }
  | { type: 'error'; payload: Error }
  | { type: 'pagination/loading'; payload: Index }
  | { type: 'pagination/error'; payload: Index }
  | {
      type: 'pagination/loaded';
      payload: { index: Index; results: SearchResults };
    };

export const initialState: State = { type: 'idle' };

export const reducer: Reducer<State, Action> = (state, action) => {
  switch (action.type) {
    case 'reset':
      return { type: 'idle' };

    case 'error':
      return { type: 'error', error: action.payload };

    case 'loading':
      return { type: 'loading' };

    case 'loaded':
      return {
        type: 'loaded',
        data: {
          results: action.payload,
          paginationStates: {} as LoadedState['paginationStates'],
        },
      };

    case 'pagination/loading':
      if (state.type !== 'loaded') return state;
      return {
        ...state,
        data: {
          ...state.data,
          paginationStates: {
            ...state.data.paginationStates,
            [action.payload]: 'loading',
          },
        },
      };

    case 'pagination/error':
      if (state.type !== 'loaded') return state;
      return {
        ...state,
        data: {
          ...state.data,
          paginationStates: {
            ...state.data.paginationStates,
            [action.payload]: 'error',
          },
        },
      };

    case 'pagination/loaded':
      if (state.type !== 'loaded') return state;
      const { index, results } = action.payload;

      const newCards = optionFromNullable(
        results.GroupedResults.find(gr => gr.Specific === index)
      )
        .map(gr => gr.Results)
        .getOrElse([]);

      const nextResults: SearchResults = {
        ...state.data.results,
        GroupedResults: [
          ...state.data.results.GroupedResults.map(gr => {
            if (gr.Specific !== index) return gr;

            return {
              ...gr,
              Results: [...gr.Results, ...newCards] as any,
              PageNumber: gr.PageNumber + 1,
            };
          }),
        ],
      };

      const nextPaginationStates = { ...state.data.paginationStates };
      delete nextPaginationStates[index];

      return {
        ...state,
        data: {
          ...state.data,
          results: nextResults,
          paginationStates: nextPaginationStates,
        },
      };
  }
};
